/**
 * CMRP selection snapshot — persists last results URL + format/purchase selections
 * (regular DDCS cards, Suggested/Choice, and Bundle) in localStorage (per shop).
 * Does not participate in URL/form restore logic; callers inject the last URL before
 * URLStateManager.initialize(), and rehydrate checkboxes / React selection stores
 * after products render.
 *
 * Snapshot is cleared after a merchant-configured inactivity window in the
 * snapshot's CMRP flow (theme settings: value + minutes/hours/days), or after
 * a successful Add to Bag. On the results page, an expired snapshot with a
 * saved course list or ATB queue prompts before wiping — only on the same
 * CMRP form as the snapshot. URL restore waits until the user chooses Keep.
 * A separate optional
 * hard-refresh window (0 disables) runs only when the user visits a CMRP
 * page: if elapsed, it wipes timestamps, saved courses, and the ATB queue
 * and strips DDCS params from the results URL (replaceState, no redirect).
 * In-page CMRP interactions (selections, Keep, etc.) do not trigger it.
 * Visiting a different flow does not delete the snapshot: course cards and
 * ATB queue stay parked until the user submits results on the new flow (or
 * opens a foreign-flow URL with course restore params). Same-flow visits
 * restore courses + queue. The inactivity stamp restarts only on interaction
 * in the snapshot's flow.
 *
 * Cross-tab: localStorage is shared. Saves merge with the latest stored selections
 * so Tab B picks up Tab A’s ATB items when adding more. `storage` events pull
 * remote additions into the live SelectionManager on other open results tabs.
 *
 * Flow preference keys (`cmrp_flow`, etc.) remain in sessionStorage.
 */
(function (window) {
  'use strict';

  const STORAGE_KEY_BASE = 'cmrp_session_snapshot';
  const SKIP_KEY_BASE = 'cmrp_skip_last_url';
  const ACTIVITY_KEY_BASE = 'cmrp_last_interaction';
  /** @deprecated previous storefront-wide stamp; dropped on read */
  const LEGACY_ACTIVITY_KEY = 'cmrp_last_storefront_activity';
  const DEFAULT_INACTIVITY_MS = 30 * 60 * 1000;
  const RESULTS_PATH = '/pages/course-materials-results';
  const TEXTBOOKS_LANDING_PATH = '/pages/textbooks-and-course-materials';

  const COURSE_PARAM_KEYS = [
    'sectionDisplayName',
    'courseDisplayName',
    'courseRefId',
    'termId',
    'shopBy',
    'departmentDisplayName',
    'divisionDisplayName',
  ];

  /** Course/campus/term restore params. `shopBy` is flow chrome, not DDCS. */
  const DDCS_PARAM_KEYS = [
    'campus',
    'term',
    'termId',
    'divisionDisplayName',
    'departmentDisplayName',
    'courseDisplayName',
    'sectionDisplayName',
    'courseRefId',
    'bookstoreId',
    'merfnbr',
  ];

  /** Pipe-aligned course slots in a results deep-link (one index per course card). */
  const PIPE_COURSE_KEYS = [
    'courseDisplayName',
    'sectionDisplayName',
    'departmentDisplayName',
    'divisionDisplayName',
    'courseRefId',
  ];

  /** @type {boolean} */
  let applyingRemoteSelections = false;

  /** Dedupe keys last written/known by this tab (for cross-tab merge vs local deselect). */
  /** @type {Set<string>} */
  let lastKnownLocalKeys = new Set();

  /** Results-page confirm is open; do not restart the inactivity stamp until Keep. */
  /** @type {boolean} */
  let expiryPromptPending = false;

  /**
   * @param {unknown} rawValue
   * @param {unknown} rawUnit
   * @returns {number}
   */
  function durationMsFromSettings(rawValue, rawUnit) {
    const value = Number(rawValue);
    const unit = String(rawUnit || 'minutes')
      .toLowerCase()
      .trim();
    if (!Number.isFinite(value) || value <= 0) return 0;
    if (unit === 'days') return value * 24 * 60 * 60 * 1000;
    if (unit === 'hours') return value * 60 * 60 * 1000;
    return value * 60 * 1000;
  }

  /** @returns {number} */
  function getInactivityMs() {
    const ms = durationMsFromSettings(
      window.shopifySettings?.cmrpSessionInactivityValue,
      window.shopifySettings?.cmrpSessionInactivityUnit
    );
    return ms > 0 ? ms : DEFAULT_INACTIVITY_MS;
  }

  /** Merchant hard-refresh window. 0 disables. */
  /** @returns {number} */
  function getHardRefreshMs() {
    return durationMsFromSettings(
      window.shopifySettings?.cmrpSessionHardRefreshValue,
      window.shopifySettings?.cmrpSessionHardRefreshUnit
    );
  }

  /** @returns {string} */
  function shopScope() {
    try {
      return String(
        window.Shopify?.shop || window.location.hostname || 'default'
      ).toLowerCase();
    } catch (e) {
      return 'default';
    }
  }

  /** @param {string} base */
  function scopedKey(base) {
    return `${base}::${shopScope()}`;
  }

  function storageKey() {
    return scopedKey(STORAGE_KEY_BASE);
  }

  /** @type {BroadcastChannel|null} */
  let snapshotChannel = null;
  /** @type {boolean} */
  let snapshotChannelTried = false;

  function ensureSnapshotChannel() {
    if (snapshotChannelTried) return snapshotChannel;
    snapshotChannelTried = true;
    try {
      snapshotChannel = new BroadcastChannel(
        `cmrp-session-snapshot::${shopScope()}`
      );
    } catch (e) {
      snapshotChannel = null;
    }
    return snapshotChannel;
  }

  function notifyOtherTabs() {
    try {
      ensureSnapshotChannel()?.postMessage({ type: 'snapshot-updated' });
    } catch (e) {
      /* ignore */
    }
  }

  /** @type {number} */
  let reconcileTimer = 0;

  /** @param {string} [reason] */
  function scheduleReconcile(reason) {
    window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(() => {
      reconcileLiveQueueFromStorage(reason);
    }, 50);
  }

  function skipKey() {
    return scopedKey(SKIP_KEY_BASE);
  }

  function activityKey() {
    return scopedKey(ACTIVITY_KEY_BASE);
  }

  /** Drop pre-localStorage session keys for this feature (no value migration). */
  function dropLegacySessionKeys() {
    try {
      sessionStorage.removeItem(STORAGE_KEY_BASE);
      sessionStorage.removeItem(SKIP_KEY_BASE);
      sessionStorage.removeItem(ACTIVITY_KEY_BASE);
      sessionStorage.removeItem(LEGACY_ACTIVITY_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  /** @returns {object} */
  function emptySnapshot() {
    return {
      lastResultsUrl: '',
      selections: [],
      flow: '',
      updatedAt: 0,
    };
  }

  /** @returns {object} */
  function readSnapshot() {
    try {
      const raw = localStorage.getItem(storageKey());
      if (!raw) return emptySnapshot();
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return emptySnapshot();
      return {
        lastResultsUrl: String(parsed.lastResultsUrl || ''),
        selections: Array.isArray(parsed.selections) ? parsed.selections : [],
        flow: normalizeSnapshotFlow(parsed.flow) || '',
        updatedAt: Number(parsed.updatedAt) || 0,
      };
    } catch (e) {
      return emptySnapshot();
    }
  }

  /**
   * @param {object} snapshot
   * @param {{ touch?: boolean }} [options]
   */
  function writeSnapshot(snapshot, options) {
    try {
      const flow =
        normalizeSnapshotFlow(snapshot.flow) ||
        inferFlowFromUrl(snapshot.lastResultsUrl) ||
        '';
      const payload = {
        lastResultsUrl: String(snapshot.lastResultsUrl || ''),
        selections: Array.isArray(snapshot.selections)
          ? snapshot.selections
          : [],
        flow,
        updatedAt: Date.now(),
      };
      localStorage.setItem(storageKey(), JSON.stringify(payload));
      notifyOtherTabs();
      const allowTouch = !options || options.touch !== false;
      if (
        allowTouch &&
        !expiryPromptPending &&
        (!flow || snapshotMatchesPageFlow(flow))
      ) {
        touchInteraction();
      }
    } catch (e) {
      /* ignore */
    }
  }

  /** @param {string} [search] */
  function hasDeepLinkCourseParams(search) {
    if (
      typeof window.URLStateManager?.hasDeepLinkCourseParamsInSearch ===
      'function'
    ) {
      return window.URLStateManager.hasDeepLinkCourseParamsInSearch(search);
    }
    const p = new URLSearchParams(search ?? '');
    return Boolean(
      p.get('sectionDisplayName') ||
        p.get('courseDisplayName') ||
        p.get('courseRefId')
    );
  }

  /** @param {string} [search] */
  function hasIntentionalLandingParams(search) {
    const p = new URLSearchParams(search ?? '');
    // `shopBy` alone is flow chrome (empty results for that form), not a
    // campus/term hydration. Treating it as landing blocked same-flow resume:
    // returning to `?shopBy=course` never injected lastResultsUrl.
    return Boolean(p.get('campus') || p.get('term') || p.get('sidError'));
  }

  /**
   * DDCS restore params from a query string. Ignores `shopBy` / `student_id`.
   * @param {string} [search]
   * @returns {Record<string, string>}
   */
  function collectDdcsParams(search) {
    const p = new URLSearchParams(search ?? '');
    /** @type {Record<string, string>} */
    const out = {};
    DDCS_PARAM_KEYS.forEach(key => {
      const val = String(p.get(key) || '').trim();
      if (val) out[key] = val;
    });
    return out;
  }

  /** @param {string} [search] */
  function ddcsParamSignature(search) {
    const map = collectDdcsParams(search);
    return Object.keys(map)
      .sort()
      .map(key => `${key}=${map[key]}`)
      .join('&');
  }

  /** @param {string} [url] */
  function searchFromUrl(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).search;
    } catch (e) {
      return '';
    }
  }

  /**
   * True when the address bar has DDCS params and they are not the same set
   * as the parked snapshot URL (`shopBy` is not part of the comparison).
   * @param {string} [incomingSearch]
   * @param {string} [snapshotUrl]
   * @returns {boolean}
   */
  function incomingDdcsDiffersFromSnapshot(incomingSearch, snapshotUrl) {
    const incoming = ddcsParamSignature(incomingSearch);
    if (!incoming) return false;
    return incoming !== ddcsParamSignature(searchFromUrl(snapshotUrl));
  }

  /** @param {string} urlOrPath */
  function normalizeResultsUrl(urlOrPath) {
    if (!urlOrPath) return '';
    try {
      const u = new URL(urlOrPath, window.location.origin);
      if (!u.pathname.includes('course-materials-results')) return '';
      // Never persist student_id in the stored URL
      u.searchParams.delete('student_id');
      const qs = u.searchParams.toString();
      return qs ? `${u.pathname}?${qs}` : u.pathname;
    } catch (e) {
      return '';
    }
  }

  /** @param {string} url */
  function urlHasRestorableCourseState(url) {
    if (!url) return false;
    try {
      const u = new URL(url, window.location.origin);
      return hasDeepLinkCourseParams(u.search);
    } catch (e) {
      return false;
    }
  }

  /**
   * Canonical snapshot flow id. `sso` is student-id. Course-id is distinct from
   * manual course-builder so a later shop-by-course-id UI parks separately.
   * @param {unknown} raw
   * @returns {string}
   */
  function normalizeSnapshotFlow(raw) {
    const flow = String(raw || '')
      .trim()
      .toLowerCase();
    if (flow === 'sso' || flow === 'student-id' || flow === 'studentid') {
      return 'student-id';
    }
    if (
      flow === 'course-builder-course-id' ||
      flow === 'course-id' ||
      flow === 'courserefid' ||
      flow === 'course-ref-id'
    ) {
      return 'course-builder-course-id';
    }
    if (flow === 'discover-view' || flow === 'discoverview') {
      return 'discover-view';
    }
    if (flow === 'course-builder' || flow === 'course') {
      return 'course-builder';
    }
    return flow;
  }

  /**
   * Infer CMRP UI flow from a results URL (`shopBy`).
   * @param {string} [url]
   * @returns {string}
   */
  function inferFlowFromUrl(url) {
    if (!url) return '';
    try {
      const u = new URL(url, window.location.origin);
      const rawShopBy = u.searchParams.get('shopBy');
      const shopByIsStudentId =
        typeof window.cmrpShopByIsStudentId === 'function'
          ? window.cmrpShopByIsStudentId(rawShopBy)
          : (() => {
              let value = String(rawShopBy || '');
              try {
                value = value ? decodeURIComponent(value) : '';
              } catch (e) {
                /* keep raw */
              }
              value = value.trim().toLowerCase();
              return value === 'studentid' || value === 'student-id';
            })();
      if (shopByIsStudentId) return 'student-id';

      const shopBy =
        typeof window.cmrpNormalizeShopByParam === 'function'
          ? window.cmrpNormalizeShopByParam(rawShopBy)
          : String(rawShopBy || '')
              .trim()
              .toLowerCase();

      // courseRefId / discoverview URLs are course-builder on this branch.
      // Distinct flows (course-id, discover-view) are stamped on the snapshot
      // / session when that UI is active.
      if (shopBy === 'courserefid') {
        try {
          const stored = normalizeSnapshotFlow(
            sessionStorage.getItem('cmrp_flow')
          );
          if (stored === 'course-builder-course-id') {
            return 'course-builder-course-id';
          }
        } catch (e) {
          /* ignore */
        }
        return 'course-builder';
      }
      if (shopBy.includes('discoverview')) {
        try {
          const stored = normalizeSnapshotFlow(
            sessionStorage.getItem('cmrp_flow')
          );
          if (stored === 'discover-view') return 'discover-view';
        } catch (e) {
          /* ignore */
        }
        return 'course-builder';
      }

      // Same rule as live restore: any non-student-id shopBy (course, discovershop,
      // discoveraccess, …) is course-builder, even with no DDCS params yet.
      const shopByMeansCourse =
        typeof window.cmrpShopByMeansCourseUrl === 'function'
          ? window.cmrpShopByMeansCourseUrl(rawShopBy)
          : Boolean(shopBy);
      if (shopByMeansCourse) return 'course-builder';

      const hasCourseData =
        typeof window.cmrpUrlHasCourseRestoreData === 'function'
          ? window.cmrpUrlHasCourseRestoreData(u.searchParams)
          : hasDeepLinkCourseParams(u.search);
      if (hasCourseData) return 'course-builder';
    } catch (e) {
      /* ignore */
    }
    return '';
  }

  /** @returns {string} */
  function detectPageFlow() {
    try {
      if (typeof window.cmrpCurrentFlow === 'function') {
        const fromFn = normalizeSnapshotFlow(window.cmrpCurrentFlow());
        if (fromFn) return fromFn;
      }
    } catch (e) {
      /* ignore */
    }
    try {
      const stored = normalizeSnapshotFlow(
        sessionStorage.getItem('cmrp_flow')
      );
      if (stored) return stored;
    } catch (e) {
      /* ignore */
    }
    try {
      const initial = normalizeSnapshotFlow(
        sessionStorage.getItem('cmrp_initial_flow')
      );
      if (initial) return initial;
    } catch (e) {
      /* ignore */
    }
    return inferFlowFromUrl(
      `${window.location.pathname}${window.location.search}`
    );
  }

  /**
   * @param {object} [snap]
   * @returns {string}
   */
  function snapshotFlowOf(snap) {
    const s = snap || readSnapshot();
    return (
      normalizeSnapshotFlow(s.flow) || inferFlowFromUrl(s.lastResultsUrl)
    );
  }

  /**
   * True when the parked snapshot belongs to the flow currently on this page.
   * Empty snapshot / unknown page flow does not hide.
   * @param {string} [explicitSnapFlow]
   * @returns {boolean}
   */
  function snapshotMatchesPageFlow(explicitSnapFlow) {
    const snapFlow =
      explicitSnapFlow != null && explicitSnapFlow !== ''
        ? normalizeSnapshotFlow(explicitSnapFlow)
        : snapshotFlowOf();
    const pageFlow = detectPageFlow();
    if (!snapFlow) return true;
    if (!pageFlow) return true;
    return snapFlow === pageFlow;
  }

  /**
   * Form the user is opening right now. URL `shopBy` wins so a leftover
   * session `cmrp_flow` cannot treat student-id as shop-by-course (or vice versa).
   * @returns {string}
   */
  function formFlowFromCurrentView() {
    const fromUrl = inferFlowFromUrl(
      `${window.location.pathname}${window.location.search}`
    );
    if (fromUrl) return fromUrl;
    return detectPageFlow();
  }

  /**
   * Expiry prompt only: both snapshot and current form must be known and equal.
   * Unknown form does not count as a match (do not prompt on the other view).
   * @returns {boolean}
   */
  function snapshotMatchesCurrentFormView() {
    const snapFlow = snapshotFlowOf();
    const formFlow = formFlowFromCurrentView();
    if (!snapFlow || !formFlow) return false;
    return snapFlow === formFlow;
  }

  /** @returns {boolean} */
  function isExpiryPromptPending() {
    return expiryPromptPending === true;
  }

  /**
   * Do not inject lastResultsUrl / run URL restore until Keep, or until we
   * know this visit is a different form (hide, no prompt).
   * @returns {boolean}
   */
  function shouldHaltUrlRestoreForExpiryPrompt() {
    if (!isCmrpResultsPage()) return false;
    if (expiryPromptPending) return true;
    if (!isInactive() || !hasPromptableSnapshot()) return false;
    const snapFlow = snapshotFlowOf();
    const formFlow = formFlowFromCurrentView();
    if (formFlow && snapFlow && formFlow !== snapFlow) return false;
    return true;
  }

  /** Persist onto this snapshot only when it belongs to the current flow. */
  function shouldPersistToSnapshot() {
    if (peekSkipRestore()) return false;
    return snapshotMatchesPageFlow();
  }

  /**
   * Decide whether an intentional flow entry (shop-by card / Find Textbooks)
   * should resume the last same-flow results URL or open a blank page for a
   * different flow (hide parked snapshot; do not delete it).
   * @param {string} targetFlow
   * @returns {{ action: 'restore'|'hide'|'fresh', clear: boolean, href: string, lastFlow: string }}
   */
  function planFlowEntry(targetFlow) {
    const normalized = normalizeSnapshotFlow(targetFlow);
    if (isHardRefreshDue()) {
      hardRefreshSession();
      return {
        action: 'fresh',
        clear: false,
        href: withShopByOnHref(RESULTS_PATH, normalized),
        lastFlow: '',
      };
    }
    clearIfInactive();
    const snap = readSnapshot();
    const last = snap.lastResultsUrl || '';
    const lastFlow = snapshotFlowOf(snap);
    const canRestore =
      Boolean(normalized) &&
      Boolean(last) &&
      urlHasRestorableCourseState(last) &&
      lastFlow === normalized;

    if (canRestore) {
      return {
        action: 'restore',
        clear: false,
        href: withShopByOnHref(last, normalized),
        lastFlow,
      };
    }

    if (lastFlow && normalized && lastFlow !== normalized) {
      return {
        action: 'hide',
        clear: false,
        href: withShopByOnHref(RESULTS_PATH, normalized),
        lastFlow,
      };
    }

    return {
      action: 'fresh',
      clear: false,
      href: withShopByOnHref(RESULTS_PATH, normalized),
      lastFlow,
    };
  }

  function markSkipRestore() {
    try {
      localStorage.setItem(skipKey(), '1');
    } catch (e) {
      /* ignore */
    }
  }

  /** @returns {boolean} */
  function consumeSkipRestore() {
    try {
      const v = localStorage.getItem(skipKey());
      if (v) {
        localStorage.removeItem(skipKey());
        return true;
      }
    } catch (e) {
      /* ignore */
    }
    return false;
  }

  /** @returns {boolean} */
  function peekSkipRestore() {
    try {
      return localStorage.getItem(skipKey()) === '1';
    } catch (e) {
      return false;
    }
  }

  /**
   * Save the current (or provided) results URL when it has course deep-link state.
   * @param {string} [url]
   */
  function saveResultsUrl(url) {
    if (!shouldPersistToSnapshot()) {
      return;
    }
    const input = url || `${window.location.pathname}${window.location.search}`;
    const normalized = normalizeResultsUrl(input);
    if (!normalized) {
      return;
    }
    if (!urlHasRestorableCourseState(normalized)) {
      return;
    }
    const snap = readSnapshot();
    const pageFlow = detectPageFlow();
    if (snap.lastResultsUrl === normalized && (!pageFlow || snap.flow === pageFlow)) {
      return;
    }
    snap.lastResultsUrl = normalized;
    if (pageFlow) snap.flow = pageFlow;
    writeSnapshot(snap);
  }

  /** @param {unknown} value */
  function strField(value) {
    return String(value ?? '').trim();
  }

  /**
   * Numeric id from a Shopify GID or plain id string.
   * @param {unknown} id
   * @returns {string}
   */
  function extractNumericId(id) {
    if (id == null || id === '') return '';
    const raw = String(id).trim();
    if (!raw) return '';
    const parts = raw.split('/');
    return parts[parts.length - 1] || raw;
  }

  /**
   * Dedupe key for persisted / in-memory selection rows.
   * Includes the course section so the same variant in another selection
   * is stored as a separate row.
   * @param {object} s
   * @returns {string}
   */
  function selectionDedupeKey(s) {
    if (!s) return '';
    const variantId = extractNumericId(s.variantId || s.variant?.id || '');
    const source = strField(s.source);
    const sectionId = strField(s.sectionId) || strField(s.instanceId);
    const productId = strField(s.productId);
    return `${variantId}|${source}|${sectionId}|${productId}`;
  }

  /**
   * Union two selection lists; `incoming` wins on the same dedupe key.
   * @param {Array<object>} stored
   * @param {Array<object>} incoming
   * @returns {Array<object>}
   */
  function mergeSelectionLists(stored, incoming) {
    const map = new Map();
    (Array.isArray(stored) ? stored : []).forEach(s => {
      const key = selectionDedupeKey(s);
      if (key) map.set(key, s);
    });
    (Array.isArray(incoming) ? incoming : []).forEach(s => {
      const key = selectionDedupeKey(s);
      if (key) map.set(key, s);
    });
    return Array.from(map.values());
  }

  /**
   * Resolve a full variant object from courseData by product + variant id only
   * (no format/purchaseType fallback).
   * @param {string} productId
   * @param {string} variantId
   * @returns {{ product: object|null, variant: object|null }}
   */
  function resolveVariantFromCourseData(productId, variantId) {
    const products = window.courseData?.products_Materials || [];
    const targetVid = extractNumericId(variantId);
    if (!targetVid) {
      return { product: null, variant: null };
    }

    const product =
      products.find(
        /** @param {any} p */ p =>
          String(p.id) === String(productId) ||
          extractNumericId(p.id) === extractNumericId(productId)
      ) || null;

    if (!product) {
      return { product: null, variant: null };
    }

    const variant =
      (product.variants || []).find(
        /** @param {any} v */ v => extractNumericId(v.id) === targetVid
      ) || null;

    return { product, variant };
  }

  /**
   * Compact variant payload so ATB can restore without the product still loaded.
   * @param {object|null|undefined} variant
   * @param {string} fallbackId
   * @param {unknown} fallbackPrice
   * @returns {object|null}
   */
  function compactVariant(variant, fallbackId, fallbackPrice) {
    const id = variant?.id || fallbackId;
    if (!id) return null;
    let metafields = [];
    if (Array.isArray(variant?.metafields)) {
      try {
        metafields = JSON.parse(JSON.stringify(variant.metafields));
      } catch (e) {
        metafields = [];
      }
    }
    const price = variant?.price != null ? variant.price : fallbackPrice;
    return {
      id,
      price: price != null && price !== '' ? price : 0,
      title: strField(variant?.title),
      option1: strField(variant?.option1),
      option2: strField(variant?.option2),
      parentProductId: strField(variant?.parentProductId),
      metafields,
    };
  }

  /**
   * Normalize raw selection rows for persistence.
   * @param {Array<object>} selections
   * @returns {Array<object>}
   */
  function normalizeSelectionsForStorage(selections) {
    if (!Array.isArray(selections)) return [];
    return selections
      .filter(s => s && (s.variantId || s.variant?.id))
      .map(s => {
        const variantId = extractNumericId(s.variantId || s.variant?.id || '');
        const source = strField(s.source);
        const sectionId = strField(s.sectionId) || strField(s.instanceId);
        const instanceId = strField(s.instanceId) || sectionId;
        return {
          variantId,
          source: source === 'suggested' || source === 'bundle' ? source : '',
          sectionId,
          instanceId,
          selectedFormat: strField(s.selectedFormat),
          productId: strField(s.productId),
          purchaseType: strField(s.purchaseType),
          productTitle: strField(s.productTitle),
          price:
            s.price != null && s.price !== ''
              ? s.price
              : s.variant?.price != null
                ? s.variant.price
                : 0,
          variant: compactVariant(s.variant, variantId, s.price),
          campus: strField(s.campus),
          term: strField(s.term),
          termId: strField(s.termId),
          division: strField(s.division),
          department: strField(s.department),
          course: strField(s.course),
          section: strField(s.section),
          bookISBN: strField(s.bookISBN),
          bookEdition: strField(s.bookEdition),
          bookCopyright: strField(s.bookCopyright),
          bookPublisher: strField(s.bookPublisher),
          bookRentDueDate: strField(s.bookRentDueDate),
          format: strField(s.format),
          type: strField(s.type),
          departmentData: strField(s.departmentData),
          subDepartment: strField(s.subDepartment),
          classds: strField(s.classds),
          subClass: strField(s.subClass),
          rentalOnlySku: strField(s.rentalOnlySku),
          subscriptionLength: strField(s.subscriptionLength),
          isDigital: strField(s.isDigital),
          publisherCode: strField(s.publisherCode),
        };
      })
      .filter(s => s.variantId);
  }

  /**
   * Persist purchase/format selections (regular + suggested/choice + bundle).
   * Merges with localStorage so other tabs’ ATB items are kept, while honoring
   * this tab’s intentional removals/clears.
   * @param {Array<object>} selections
   * @param {{ replace?: boolean }} [options]
   */
  function saveSelections(selections, options) {
    if (!shouldPersistToSnapshot()) {
      return;
    }
    if (applyingRemoteSelections) {
      return;
    }
    const incoming = normalizeSelectionsForStorage(selections);
    const replace = Boolean(options && options.replace);

    const snap = readSnapshot();
    const pageFlow = detectPageFlow();
    let merged;
    if (replace) {
      merged = incoming;
    } else {
      const incomingKeys = new Set(incoming.map(selectionDedupeKey).filter(Boolean));
      const removedByThisTab = new Set(
        [...lastKnownLocalKeys].filter(key => !incomingKeys.has(key))
      );
      const storedKept = (snap.selections || []).filter(s => {
        const key = selectionDedupeKey(s);
        return key && !removedByThisTab.has(key);
      });
      merged = mergeSelectionLists(storedKept, incoming);
    }

    snap.selections = merged;
    if (pageFlow) snap.flow = pageFlow;
    writeSnapshot(snap);
    lastKnownLocalKeys = new Set(merged.map(selectionDedupeKey).filter(Boolean));

    // If storage had items this tab did not know about, pull them into the live manager.
    if (!replace) {
      pullMissingSelectionsIntoLiveManager(merged);
    }
  }

  /** @returns {object|null} */
  function getLiveSelectionManager() {
    return (
      window.courseMaterialsPage?.components?.selectionManager ||
      window.courseMaterialsPageInstance?.components?.selectionManager ||
      null
    );
  }

  /** @param {Set<string>} a @param {Set<string>} b */
  function selectionKeySetsEqual(a, b) {
    if (a.size !== b.size) return false;
    for (const key of a) {
      if (!b.has(key)) return false;
    }
    return true;
  }

  /**
   * Drop Suggested/Choice/Bundle window-store rows whose dedupe key is not in
   * the stored queue (other tab removed or swapped them).
   * @param {string} storeName
   * @param {string} listenersName
   * @param {'suggested'|'bundle'} source
   * @param {Set<string>} keepKeys
   */
  function pruneReactSelectionStore(storeName, listenersName, source, keepKeys) {
    const store = typeof window !== 'undefined' ? window[storeName] : null;
    if (!Array.isArray(store) || !store.length) return;
    const next = store.filter(entry => {
      const variant = entry?.variant || entry;
      const key = selectionDedupeKey({
        variantId: variant?.id,
        variant,
        source,
        sectionId: entry?.sectionId,
        instanceId: entry?.instanceId,
        productId: entry?.productId || variant?.parentProductId,
      });
      return key && keepKeys.has(key);
    });
    if (next.length === store.length) return;
    window[storeName] = next;
    (window[listenersName] || []).forEach(/** @param {Function} listener */ listener => {
      try {
        listener();
      } catch (e) {
        /* ignore */
      }
    });
  }

  /**
   * Uncheck purchase boxes that no longer match the live queue (other tab
   * deselected / swapped a variant).
   * @param {object} selectionManager
   */
  function uncheckBoxesNotInLiveQueue(selectionManager) {
    const items = selectionManager.selectedProducts || [];
    const matcher =
      typeof selectionManager._checkboxMatchesSnapshotItem === 'function'
        ? selectionManager._checkboxMatchesSnapshotItem.bind(selectionManager)
        : null;
    document
      .querySelectorAll(
        '.purchase-checkbox:checked, .purchase-choice-checkbox:checked'
      )
      .forEach(/** @param {HTMLInputElement} checkbox */ checkbox => {
        const stillSelected = matcher
          ? items.some(item => matcher(checkbox, item))
          : items.some(item => {
              const cbVid = extractNumericId(
                checkbox.getAttribute('data-variant-id') || checkbox.value
              );
              return (
                cbVid &&
                extractNumericId(item.variant?.id || item.variantId) === cbVid
              );
            });
        if (!stillSelected) checkbox.checked = false;
      });
  }

  /**
   * Make this tab's ATB queue + sticky banner match localStorage.
   *
   * Discrepancies this covers:
   * - other tab added items
   * - other tab removed some items
   * - other tab emptied the queue (ATB / clear)
   * - other tab swapped a variant (old key gone, new key present)
   * - storage `storage` event missed while this tab was backgrounded
   *
   * Does not write localStorage (storage is the source of truth).
   */
  function reconcileLiveQueueFromStorage(reason) {
    const skip =
      !isCmrpResultsPage()
        ? 'not-cmrp-results'
        : !snapshotMatchesPageFlow()
          ? 'flow-mismatch'
          : applyingRemoteSelections
            ? 'already-applying'
            : null;
            
    if (skip) {
      return;
    }

    clearIfInactive();
    const stored = readSnapshot().selections || [];
    const storedKeys = new Set(stored.map(selectionDedupeKey).filter(Boolean));

    const selectionManager = getLiveSelectionManager();
    if (!selectionManager) {
      if (document.readyState === 'loading') {
        document.addEventListener(
          'DOMContentLoaded',
          () => reconcileLiveQueueFromStorage(reason || 'dom-ready'),
          { once: true }
        );
      }
      return;
    }

    const liveKeys = new Set(
      (selectionManager.selectedProducts || [])
        .map(selectionDedupeKey)
        .filter(Boolean)
    );
    if (selectionKeySetsEqual(liveKeys, storedKeys)) {
      if (typeof selectionManager.updateSelectionBarSummary === 'function') {
        selectionManager.updateSelectionBarSummary();
      }
      return;
    }

    applyingRemoteSelections = true;
    try {
      if (!storedKeys.size) {
        if (typeof selectionManager.clearSelections === 'function') {
          selectionManager.clearSelections();
        } else {
          selectionManager.selectedProducts = [];
        }
        lastKnownLocalKeys = new Set();
        return;
      }

      selectionManager.selectedProducts = (
        selectionManager.selectedProducts || []
      ).filter(item => storedKeys.has(selectionDedupeKey(item)));

      pruneReactSelectionStore(
        'selectedSuggestedMaterials',
        'selectedSuggestedMaterialsListeners',
        'suggested',
        storedKeys
      );
      pruneReactSelectionStore(
        'selectedBundleMaterials',
        'selectedBundleMaterialsListeners',
        'bundle',
        storedKeys
      );

      const afterDropKeys = new Set(
        (selectionManager.selectedProducts || [])
          .map(selectionDedupeKey)
          .filter(Boolean)
      );
      const missing = stored.filter(row => {
        const key = selectionDedupeKey(row);
        return key && !afterDropKeys.has(key);
      });
      if (missing.length) {
        appendSnapshotRowsToManager(selectionManager, missing);
      }

      lastKnownLocalKeys = new Set(
        (selectionManager.selectedProducts || [])
          .map(selectionDedupeKey)
          .filter(Boolean)
      );

      uncheckBoxesNotInLiveQueue(selectionManager);
      if (typeof selectionManager.rehydrateCheckboxSelections === 'function') {
        selectionManager.rehydrateCheckboxSelections();
      }
      if (typeof window.cmrpSyncPurchaseTypeSelectedClass === 'function') {
        window.cmrpSyncPurchaseTypeSelectedClass(document);
      }
      if (typeof selectionManager.updateSelectionBarSummary === 'function') {
        selectionManager.updateSelectionBarSummary();
      }
      if (typeof selectionManager.dispatchSelectionChangeEvent === 'function') {
        selectionManager.dispatchSelectionChangeEvent();
      }
    } finally {
      applyingRemoteSelections = false;
    }
  }

  /**
   * Add snapshot rows missing from SelectionManager (cross-tab collect-all).
   * @param {Array<object>} mergedStored
   */
  function pullMissingSelectionsIntoLiveManager(mergedStored) {
    if (!snapshotMatchesPageFlow()) return;
    const selectionManager = getLiveSelectionManager();
    if (!selectionManager || !Array.isArray(mergedStored) || !mergedStored.length) {
      return;
    }

    const existingKeys = new Set(
      (selectionManager.selectedProducts || []).map(selectionDedupeKey)
    );
    const missing = mergedStored.filter(s => {
      const key = selectionDedupeKey(s);
      return key && !existingKeys.has(key);
    });
    if (!missing.length) return;

    applyingRemoteSelections = true;
    try {
      const added = appendSnapshotRowsToManager(selectionManager, missing);
      if (added) {
        lastKnownLocalKeys = new Set(
          (selectionManager.selectedProducts || [])
            .map(selectionDedupeKey)
            .filter(Boolean)
        );
        if (typeof selectionManager.updateSelectionBarSummary === 'function') {
          selectionManager.updateSelectionBarSummary();
        }
        if (typeof selectionManager.rehydrateCheckboxSelections === 'function') {
          selectionManager.rehydrateCheckboxSelections();
        }
        if (typeof selectionManager.dispatchSelectionChangeEvent === 'function') {
          selectionManager.dispatchSelectionChangeEvent();
        } else {
          document.dispatchEvent(
            new CustomEvent('selection:changed', {
              detail: {
                selectedProducts: selectionManager.getSelectedProducts
                  ? selectionManager.getSelectedProducts()
                  : selectionManager.selectedProducts,
              },
            })
          );
        }
      }
    } finally {
      applyingRemoteSelections = false;
    }
  }

  /**
   * Cart line fields stored on a snapshot row (same set regular cards use at ATB).
   * @param {object} s
   * @returns {object}
   */
  function cartPropsFromSnap(s) {
    return {
      campus: strField(s.campus),
      term: strField(s.term),
      termId: strField(s.termId),
      division: strField(s.division),
      department: strField(s.department),
      course: strField(s.course),
      section: strField(s.section),
      bookISBN: strField(s.bookISBN),
      bookEdition: strField(s.bookEdition),
      bookCopyright: strField(s.bookCopyright),
      bookPublisher: strField(s.bookPublisher),
      bookRentDueDate: strField(s.bookRentDueDate),
      format: strField(s.format),
      type: strField(s.type),
      departmentData: strField(s.departmentData),
      subDepartment: strField(s.subDepartment),
      classds: strField(s.classds),
      subClass: strField(s.subClass),
      rentalOnlySku: strField(s.rentalOnlySku),
      subscriptionLength: strField(s.subscriptionLength),
      isDigital: strField(s.isDigital),
      publisherCode: strField(s.publisherCode),
    };
  }

  /**
   * @param {object} selectionManager
   * @param {Array<object>} snapRows
   * @returns {boolean} true when at least one row was appended
   */
  function appendSnapshotRowsToManager(selectionManager, snapRows) {
    /** @type {Array<{variant: object, productId: string}>} */
    const restoredSuggested = [];
    /** @type {Array<{variant: object, productId: string}>} */
    const restoredBundle = [];
    let addedRegular = false;

    const existingKeys = new Set(
      (selectionManager.selectedProducts || [])
        .map(selectionDedupeKey)
        .filter(Boolean)
    );

    snapRows.forEach(s => {
      if (existingKeys.has(selectionDedupeKey(s))) return;
      const live = resolveVariantFromCourseData(s.productId, s.variantId);
      const storedVariant = s.variant && typeof s.variant === 'object' ? s.variant : null;
      const variant =
        live.variant ||
        (storedVariant
          ? {
              ...storedVariant,
              id: storedVariant.id || s.variantId,
              price:
                storedVariant.price != null && storedVariant.price !== ''
                  ? storedVariant.price
                  : s.price != null
                    ? s.price
                    : 0,
            }
          : null);
      const product = live.product;
      if (!variant) return;

      const rawFormat = String(s.selectedFormat || '').trim().toLowerCase();
      const selectedFormat =
        rawFormat && rawFormat !== 'simple'
          ? rawFormat
          : rawFormat === 'simple'
            ? 'simple'
            : '';
      const productId = strField(s.productId) || strField(product?.id);
      const source = strField(s.source);
      const sectionId = strField(s.sectionId) || strField(s.instanceId);
      const instanceId = strField(s.instanceId) || sectionId;

      if (source === 'suggested' || source === 'bundle') {
        const entry = {
          variant: {
            ...variant,
            parentProductId: variant.parentProductId || productId,
          },
          productId,
          sectionId,
          instanceId,
        };
        if (source === 'bundle') restoredBundle.push(entry);
        else restoredSuggested.push(entry);
        selectionManager.selectedProducts.push({
          productId,
          productTitle: strField(s.productTitle) || product?.title || '',
          selectedFormat,
          purchaseType: s.purchaseType,
          sectionId,
          instanceId,
          variant,
          price: parseFloat(variant.price != null ? variant.price : s.price) || 0,
          source,
          ...cartPropsFromSnap(s),
        });
        existingKeys.add(
          selectionDedupeKey(
            selectionManager.selectedProducts[
              selectionManager.selectedProducts.length - 1
            ]
          )
        );
        addedRegular = true;
        return;
      }

      selectionManager.selectedProducts.push({
        productId,
        productTitle: strField(s.productTitle) || product?.title || '',
        selectedFormat,
        purchaseType: s.purchaseType,
        sectionId,
        instanceId,
        variant,
        price: parseFloat(variant.price != null ? variant.price : s.price) || 0,
        ...cartPropsFromSnap(s),
      });
      existingKeys.add(
        selectionDedupeKey(
          selectionManager.selectedProducts[
            selectionManager.selectedProducts.length - 1
          ]
        )
      );
      addedRegular = true;
    });

    if (restoredSuggested.length) {
      seedReactSelectionStore(
        'selectedSuggestedMaterials',
        'selectedSuggestedMaterialsListeners',
        restoredSuggested
      );
      if (typeof selectionManager.syncSuggestedMaterials === 'function') {
        selectionManager.syncSuggestedMaterials();
      }
    }
    if (restoredBundle.length) {
      seedReactSelectionStore(
        'selectedBundleMaterials',
        'selectedBundleMaterialsListeners',
        restoredBundle
      );
      if (typeof selectionManager.syncBundleMaterials === 'function') {
        selectionManager.syncBundleMaterials();
      }
    }

    return (
      addedRegular || restoredSuggested.length > 0 || restoredBundle.length > 0
    );
  }

  /**
   * Seed a React selection window store and notify listeners (Suggested/Choice/Bundle UI).
   * @param {string} storeName
   * @param {string} listenersName
   * @param {Array<{variant: object, productId: string, sectionId?: string, instanceId?: string}>} entries
   */
  function seedReactSelectionStore(storeName, listenersName, entries) {
    if (typeof window === 'undefined' || !Array.isArray(entries) || !entries.length) {
      return;
    }
    if (!window[storeName]) {
      window[storeName] = [];
    }
    const store = window[storeName];
    entries.forEach(entry => {
      if (!entry?.variant) return;
      const productId = strField(entry.productId);
      const variantId = extractNumericId(entry.variant.id);
      if (!variantId) return;
      const sectionId = strField(entry.sectionId) || strField(entry.instanceId);
      const exists = store.some(item => {
        const itemVariant = item?.variant || item;
        const itemPid = strField(
          item?.productId || itemVariant?.parentProductId || ''
        );
        const itemSection = strField(item?.sectionId) || strField(item?.instanceId);
        return (
          extractNumericId(itemVariant?.id) === variantId &&
          (!productId || !itemPid || itemPid === productId) &&
          (!sectionId || !itemSection || itemSection === sectionId)
        );
      });
      if (!exists) {
        store.push({
          variant: entry.variant,
          productId: productId || entry.variant.parentProductId || '',
          sectionId,
          instanceId: strField(entry.instanceId) || sectionId,
        });
      }
    });
    (window[listenersName] || []).forEach(
      /** @param {Function} listener */ listener => {
        try {
          listener();
        } catch (e) {
          /* ignore */
        }
      }
    );
  }

  /** @returns {Array<object>} */
  function getSelections() {
    clearIfInactive();
    return readSnapshot().selections.slice();
  }

  /** @returns {string} */
  function getLastResultsUrl() {
    clearIfInactive();
    return readSnapshot().lastResultsUrl || '';
  }

  function clear() {
    try {
      localStorage.removeItem(storageKey());
      localStorage.removeItem(activityKey());
    } catch (e) {
      /* ignore */
    }
    lastKnownLocalKeys = new Set();
    dropLegacySessionKeys();
  }

  /** Clear snapshot + skip auto-restore (intentional fresh start / landing entry). */
  function clearForFreshStart() {
    clear();
    markSkipRestore();
  }

  /**
   * Drop only the stored results deep-link URL (keep ATB selections).
   */
  function clearLastResultsUrl() {
    const snap = readSnapshot();
    if (!snap.lastResultsUrl) return;
    snap.lastResultsUrl = '';
    writeSnapshot(snap);
  }

  /** @param {unknown} value */
  function normalizeCourseLabel(value) {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');
  }

  /**
   * @param {{ courseDisplayName?: string, sectionDisplayName?: string, departmentDisplayName?: string, courseRefId?: string, termId?: string, storeNumber?: string }} slot
   * @param {object} course
   * @returns {boolean}
   */
  function snapshotSlotMatchesCourse(slot, course) {
    if (!slot || !course) return false;

    const courseTerm = strField(course.termId || course.selectedTermId);
    const slotTerm = strField(slot.termId);
    if (courseTerm && slotTerm && courseTerm !== slotTerm) return false;

    const courseStore = strField(course.storeNumber || course.store_number);
    const slotStore = strField(slot.storeNumber);
    if (courseStore && slotStore && courseStore !== slotStore) return false;

    const targetRef = strField(course.courseRefId);
    const targetSectionId = strField(
      course.sectionId || course.selectedsectionId
    );
    const slotRef = strField(slot.courseRefId);
    if (targetRef && slotRef && slotRef === targetRef) return true;
    if (targetSectionId && slotRef && slotRef === targetSectionId) return true;

    const courseName = normalizeCourseLabel(
      course.course || course.selectedcourse
    );
    const sectionName = normalizeCourseLabel(
      course.section || course.selectedsection
    );
    if (!courseName || !sectionName) return false;
    if (
      normalizeCourseLabel(slot.courseDisplayName) !== courseName ||
      normalizeCourseLabel(slot.sectionDisplayName) !== sectionName
    ) {
      return false;
    }
    const deptName = normalizeCourseLabel(
      course.department || course.selectedDepartment
    );
    const slotDept = normalizeCourseLabel(slot.departmentDisplayName);
    if (deptName && slotDept && slotDept !== deptName) return false;
    return true;
  }

  /**
   * @param {string|null|undefined} raw
   * @returns {string[]}
   */
  function splitRawPipe(raw) {
    if (raw == null || raw === '') return [];
    return String(raw).split('|');
  }

  /**
   * Pad a multi-value pipe list to course-line count (repeat last).
   * Leave 0–1 part lists alone so a shared term/store is not index-stripped.
   * @param {string[]} parts
   * @param {number} lineCount
   * @returns {string[]}
   */
  function padMultiPipeParts(parts, lineCount) {
    if (!Array.isArray(parts) || parts.length <= 1 || lineCount <= 0) {
      return Array.isArray(parts) ? parts : [];
    }
    const out = parts.slice(0, lineCount);
    while (out.length < lineCount) {
      out.push(parts[parts.length - 1] ?? '');
    }
    return out;
  }

  /**
   * @param {URLSearchParams} searchParams
   * @param {string} key
   * @param {string[]} parts
   */
  function setPipedSearchParam(searchParams, key, parts) {
    const next = (parts || []).map(part => String(part ?? '')).join('|');
    if (!next || next.split('|').every(part => !String(part).trim())) {
      searchParams.delete(key);
    } else {
      searchParams.set(key, next);
    }
  }

  /**
   * Remove one course card's slot from a results deep-link URL.
   * Also drops the matching termId / bookstoreId / merfnbr slot when those
   * params are pipe-separated (parent+child Discover).
   * @param {string} url
   * @param {object} course
   * @returns {string}
   */
  function stripCourseFromResultsUrl(url, course) {
    if (!url || !course) return url || '';
    try {
      const u = new URL(url, window.location.origin);
      const arrays = PIPE_COURSE_KEYS.map(key => {
        const raw = u.searchParams.get(key);
        return raw == null || raw === '' ? [] : raw.split('|');
      });
      const len = Math.max(0, ...arrays.map(arr => arr.length));
      if (len === 0) return url;

      const termParts = padMultiPipeParts(
        splitRawPipe(u.searchParams.get('termId')),
        len
      );
      const bookstoreParts = padMultiPipeParts(
        splitRawPipe(u.searchParams.get('bookstoreId')),
        len
      );
      const merfnbrParts = padMultiPipeParts(
        splitRawPipe(u.searchParams.get('merfnbr')),
        len
      );
      const storeParts = merfnbrParts.length > 1 ? merfnbrParts : bookstoreParts;

      const keep = [];
      for (let i = 0; i < len; i++) {
        const slot = {
          courseDisplayName: arrays[0][i] || '',
          sectionDisplayName: arrays[1][i] || '',
          departmentDisplayName: arrays[2][i] || '',
          divisionDisplayName: arrays[3][i] || '',
          courseRefId: arrays[4][i] || '',
          termId: termParts.length > 1 ? termParts[i] || '' : '',
          storeNumber: storeParts.length > 1 ? storeParts[i] || '' : '',
        };
        if (!snapshotSlotMatchesCourse(slot, course)) keep.push(i);
      }
      if (keep.length === len) return url;
      if (keep.length === 0) return '';

      PIPE_COURSE_KEYS.forEach((key, keyIndex) => {
        const src = arrays[keyIndex];
        if (!src.length) return;
        setPipedSearchParam(
          u.searchParams,
          key,
          keep.map(i => (src[i] != null ? src[i] : ''))
        );
      });
      if (termParts.length > 1) {
        setPipedSearchParam(
          u.searchParams,
          'termId',
          keep.map(i => termParts[i] ?? '')
        );
      }
      if (bookstoreParts.length > 1) {
        setPipedSearchParam(
          u.searchParams,
          'bookstoreId',
          keep.map(i => bookstoreParts[i] ?? '')
        );
      }
      if (merfnbrParts.length > 1) {
        setPipedSearchParam(
          u.searchParams,
          'merfnbr',
          keep.map(i => merfnbrParts[i] ?? '')
        );
      }

      const qs = u.searchParams.toString();
      const nextUrl = qs ? `${u.pathname}?${qs}` : u.pathname;
      const normalized = normalizeResultsUrl(nextUrl);
      return urlHasRestorableCourseState(normalized) ? normalized : '';
    } catch (e) {
      return url;
    }
  }

  /**
   * Drop a deleted course card from the stored results URL so hydrate
   * does not restore it. Clears the URL when no restorable courses remain.
   * @param {object} course
   */
  function removeCourseFromLastResultsUrl(course) {
    if (!course) return;
    const snap = readSnapshot();
    if (!snap.lastResultsUrl) return;
    const next = stripCourseFromResultsUrl(snap.lastResultsUrl, course);
    if (next === snap.lastResultsUrl) return;
    snap.lastResultsUrl = next || '';
    writeSnapshot(snap);
  }

  /**
   * Flow switch with full navigation: do not delete the parked snapshot.
   * Skip restore only when the *destination* is a different flow (hide).
   * Switching back to the parked flow must reinject lastResultsUrl.
   * @param {string} [destinationFlow]
   */
  function prepareFlowSwitch(destinationFlow) {
    const dest = normalizeSnapshotFlow(destinationFlow);
    const snapFlow = snapshotFlowOf();
    if (dest && snapFlow && dest === snapFlow) {
      consumeSkipRestore();
      return;
    }
    if (dest && snapFlow && dest !== snapFlow) {
      markSkipRestore();
      return;
    }
    if (snapshotMatchesPageFlow()) {
      return;
    }
    markSkipRestore();
  }

  /**
   * Current page is about to display results for this flow. If the parked
   * snapshot belongs to a different flow, replace it. If it has no flow yet,
   * stamp the current one without clearing selections.
   * @returns {boolean} true when a foreign snapshot was replaced
   */
  function adoptIfForeignFlow() {
    const pageFlow = detectPageFlow();
    if (!pageFlow) return false;
    const snap = readSnapshot();
    const snapFlow = snapshotFlowOf(snap);
    if (!snapFlow) {
      snap.flow = pageFlow;
      writeSnapshot(snap);
      return false;
    }
    if (snapFlow === pageFlow) return false;

    const next = emptySnapshot();
    next.flow = pageFlow;
    writeSnapshot(next);
    lastKnownLocalKeys = new Set();

    const selectionManager =
      window.courseMaterialsPage?.components?.selectionManager ||
      window.courseMaterialsPageInstance?.components?.selectionManager;
    if (selectionManager && typeof selectionManager.clearSelections === 'function') {
      selectionManager.clearSelections();
    }
    return true;
  }

  /** @returns {boolean} */
  function isCmrpResultsPage() {
    const path = window.location.pathname || '';
    return path.includes('course-materials-results');
  }

  /** Results, textbooks landing, or discover-view — a CMRP visit, not an in-page action. */
  /** @returns {boolean} */
  function isCmrpPage() {
    const path = window.location.pathname || '';
    return (
      path.includes('course-materials-results') ||
      path.includes('textbooks-and-course-materials') ||
      path.includes('follett-discover-view')
    );
  }

  /** @returns {number} */
  function readLastInteraction() {
    try {
      return Number(localStorage.getItem(activityKey())) || 0;
    } catch (e) {
      return 0;
    }
  }

  /** Restart the CMRP inactivity clock. */
  function touchInteraction() {
    try {
      localStorage.setItem(activityKey(), String(Date.now()));
      sessionStorage.removeItem(LEGACY_ACTIVITY_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  /** @returns {boolean} */
  function isInactive() {
    const last = readLastInteraction();
    return last > 0 && Date.now() - last > getInactivityMs();
  }

  /** @returns {boolean} */
  function isHardRefreshDue() {
    const windowMs = getHardRefreshMs();
    if (windowMs <= 0) return false;
    const last = readLastInteraction();
    return last > 0 && Date.now() - last > windowMs;
  }

  /** @returns {boolean} */
  function hasPromptableSnapshot() {
    const snap = readSnapshot();
    const lastUrl = snap.lastResultsUrl || '';
    const hasCourses = Boolean(lastUrl) && urlHasRestorableCourseState(lastUrl);
    const hasQueue = Array.isArray(snap.selections) && snap.selections.length > 0;
    return hasCourses || hasQueue;
  }

  /** @param {number} lastMs */
  function formatElapsedSince(lastMs) {
    const ms = Math.max(0, Date.now() - Number(lastMs || 0));
    const minutes = Math.max(1, Math.floor(ms / 60000));
    const hours = Math.floor(ms / 3600000);
    const days = Math.floor(ms / 86400000);
    if (days >= 1) return days === 1 ? '1 day' : `${days} days`;
    if (hours >= 1) return hours === 1 ? '1 hour' : `${hours} hours`;
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  }

  function shopByParamForFlow(flow) {
    const normalized = normalizeSnapshotFlow(flow);
    if (normalized === 'student-id' || normalized === 'sso') return 'studentid';
    if (normalized === 'course-builder-course-id') return 'courseRefId';
    if (normalized === 'discover-view') return 'discoverview';
    if (normalized === 'course-builder') return 'course';
    return '';
  }

  function withShopByOnHref(href, flow) {
    try {
      const u = new URL(href || RESULTS_PATH, window.location.origin);
      if (!u.searchParams.get('shopBy')) {
        const shopBy = shopByParamForFlow(flow);
        if (shopBy) u.searchParams.set('shopBy', shopBy);
      }
      return `${u.pathname}${u.search}`;
    } catch (e) {
      return href || RESULTS_PATH;
    }
  }

  /** Keep shopBy (current flow) and drop DDCS course/campus/term params. */
  function buildFreshResultsHref() {
    const path = window.location.pathname || RESULTS_PATH;
    let shopBy = '';
    try {
      shopBy = String(
        new URLSearchParams(window.location.search).get('shopBy') || ''
      ).trim();
    } catch (e) {
      shopBy = '';
    }
    if (!shopBy) {
      shopBy = shopByParamForFlow(detectPageFlow() || snapshotFlowOf());
    }
    if (!shopBy) return path;
    const params = new URLSearchParams();
    params.set('shopBy', shopBy);
    return `${path}?${params.toString()}`;
  }

  /**
   * Remove snapshot + inactivity keys this feature owns.
   * Leaves cmrp_flow / student-id session keys so the same shopBy UI stays.
   * Does not touch the skip flag (caller sets that to block pagehide restore).
   */
  function clearPersistenceStorage() {
    clear();
    try {
      localStorage.removeItem(STORAGE_KEY_BASE);
      localStorage.removeItem(ACTIVITY_KEY_BASE);
      localStorage.removeItem(LEGACY_ACTIVITY_KEY);
    } catch (e) {
      /* ignore */
    }
    dropLegacySessionKeys();
  }

  function hydrateQueueAfterExpiryKeep() {
    const selectionManager =
      window.courseMaterialsPage?.components?.selectionManager ||
      window.courseMaterialsPageInstance?.components?.selectionManager;
    if (
      !selectionManager
    ) {
      return;
    }
    hydrateSelectionManager(selectionManager);
    if (typeof selectionManager.updateSelectionBarSummary === 'function') {
      selectionManager.updateSelectionBarSummary();
    }
  }

  function resumeUrlRestoreAfterExpiryKeep() {
    const tryResume = attempts => {
      const urlManager =
        window.courseMaterialsPageInstance?.components?.urlManager ||
        window.courseMaterialsPage?.components?.urlManager;
      if (
        urlManager &&
        typeof urlManager.resumeRestoreAfterExpiryKeep === 'function'
      ) {
        urlManager.resumeRestoreAfterExpiryKeep();
        hydrateQueueAfterExpiryKeep();
        return;
      }
      if (attempts >= 40) {
        hydrateQueueAfterExpiryKeep();
        return;
      }
      window.setTimeout(() => tryResume(attempts + 1), 50);
    };
    tryResume(0);
  }

  function keepExpiredSession() {
    expiryPromptPending = false;
    touchInteraction();
    tryRestoreResultsUrl();
    resumeUrlRestoreAfterExpiryKeep();
  }

  /**
   * Incoming URL is a new DDCS selection: do not ask to resume the old session.
   * Keep the ATB queue and restart the inactivity clock for this visit.
   */
  function skipExpiryPromptKeepBag() {
    if (!expiryPromptPending) return;
    expiryPromptPending = false;
    if (typeof window.CmrpSessionExpiryModal?.close === 'function') {
      window.CmrpSessionExpiryModal.close();
    }
    touchInteraction();
  }

  /**
   * Address-bar DDCS wins: persist that URL, keep queued selections.
   */
  function adoptIncomingDdcsResultsUrl() {
    const input = `${window.location.pathname}${window.location.search}`;
    const normalized = normalizeResultsUrl(input);
    if (normalized && urlHasRestorableCourseState(normalized)) {
      const snap = readSnapshot();
      const pageFlow = detectPageFlow();
      snap.lastResultsUrl = normalized;
      if (pageFlow) snap.flow = pageFlow;
      writeSnapshot(snap, { touch: false });
    }
    skipExpiryPromptKeepBag();
  }

  function clearExpiredSessionAndReload() {
    expiryPromptPending = false;
    markSkipRestore();
    clearPersistenceStorage();
    window.location.replace(buildFreshResultsHref());
  }

  /**
   * Wipe CMRP snapshot timestamps, saved courses, and ATB queue. On results
   * URLs that still have course params, strip them in-place (replaceState)
   * before later CMRP scripts read location.search — no second navigation.
   */
  function hardRefreshSession() {
    expiryPromptPending = false;
    if (typeof window.CmrpSessionExpiryModal?.close === 'function') {
      window.CmrpSessionExpiryModal.close();
    }
    const freshHref = buildFreshResultsHref();
    const hadCourseParams =
      isCmrpResultsPage() && hasDeepLinkCourseParams(window.location.search);
    markSkipRestore();
    clearPersistenceStorage();
    if (hadCourseParams) {
      try {
        window.history.replaceState({}, '', freshHref);
      } catch (e) {
        /* ignore */
      }
    }
  }

  /**
   * Apply the no-confirm hard refresh when its window has elapsed on a CMRP
   * visit. In-page interactions must not call this.
   * @returns {boolean} true when the snapshot was hard-wiped
   */
  function applyHardRefreshIfDue() {
    if (!isCmrpPage()) return false;
    if (!isHardRefreshDue()) return false;
    hardRefreshSession();
    return true;
  }

  function promptExpiredSessionIfNeeded() {
    if (!expiryPromptPending) return;
    let attempts = 0;
    const open = () => {
      if (typeof window.CmrpSessionExpiryModal?.open === 'function') {
        window.CmrpSessionExpiryModal.open({
          elapsedLabel: formatElapsedSince(readLastInteraction()),
          onKeep: keepExpiredSession,
          onClear: clearExpiredSessionAndReload,
        });
        return;
      }
      attempts += 1;
      if (attempts > 40) {
        keepExpiredSession();
        return;
      }
      window.setTimeout(open, 50);
    };
    open();
  }

  /**
   * Wipe the snapshot when inactivity exceeds the merchant-configured window.
   * On the results page, an expired snapshot with courses or queue is held for
   * the confirm modal instead of wiping. Other pages leave the snapshot so
   * results can prompt. Does not refresh the stamp.
   * Missing stamp (first visit / pre-upgrade session) does not clear.
   * Hard-refresh expiry is handled separately by applyHardRefreshIfDue().
   * @returns {boolean} true when the snapshot was cleared for inactivity
   */
  function clearIfInactive() {
    if (!isInactive()) return false;
    if (!isCmrpResultsPage()) return false;
    if (!snapshotMatchesCurrentFormView()) return false;
    if (hasPromptableSnapshot()) {
      expiryPromptPending = true;
      return false;
    }
    clear();
    try {
      localStorage.removeItem(activityKey());
      localStorage.removeItem(skipKey());
    } catch (e) {
      /* ignore */
    }
    return true;
  }

  function releaseUrlRestoreAwaitingExpiryKeep() {
    const urlManager =
      window.courseMaterialsPageInstance?.components?.urlManager ||
      window.courseMaterialsPage?.components?.urlManager;
    if (urlManager) {
      urlManager._urlRestoreAwaitingExpiryKeep = false;
    }
  }

  /**
   * Head init can run before shopBy/session flow is settled. Re-check on ready
   * so we only prompt on the same form view, and never restore first.
   */
  function resolveExpiryPromptOnReady() {
    if (expiryPromptPending && !snapshotMatchesCurrentFormView()) {
      expiryPromptPending = false;
      releaseUrlRestoreAwaitingExpiryKeep();
      return;
    }
    if (
      !expiryPromptPending &&
      isCmrpResultsPage() &&
      isInactive() &&
      hasPromptableSnapshot() &&
      snapshotMatchesCurrentFormView()
    ) {
      expiryPromptPending = true;
    }
    if (expiryPromptPending) {
      promptExpiredSessionIfNeeded();
      return;
    }
    releaseUrlRestoreAwaitingExpiryKeep();
  }

  /** @type {boolean} */
  let skipConsumeScheduled = false;

  /**
   * Keep the fresh-start flag for every early tryRestore on the destination page
   * (head + template + page init), then drop it after load so a later bare visit
   * can restore again.
   */
  function scheduleConsumeSkipRestore() {
    if (skipConsumeScheduled || !peekSkipRestore()) return;
    skipConsumeScheduled = true;
    const run = () => {
      consumeSkipRestore();
      skipConsumeScheduled = false;
    };
    if (document.readyState === 'complete') {
      setTimeout(run, 0);
    } else {
      window.addEventListener('load', () => setTimeout(run, 0), { once: true });
    }
  }

  /**
   * If the results page loaded without course deep-link params, reinject the
   * last session URL via replaceState so existing URL restore runs unchanged.
   * @returns {boolean} true when the address bar was updated
   */
  function tryRestoreResultsUrl() {
    if (isHardRefreshDue()) {
      hardRefreshSession();
      return false;
    }
    const current = `${window.location.pathname}${window.location.search}`;
    const path = window.location.pathname || '';
    const onResults = path.includes('course-materials-results');

    const snap = readSnapshot();
    if (
      onResults &&
      incomingDdcsDiffersFromSnapshot(window.location.search, snap.lastResultsUrl)
    ) {
      if (!peekSkipRestore()) {
        const snapFlow = snapshotFlowOf(snap);
        const urlFlow = inferFlowFromUrl(current) || detectPageFlow();
        if (
          snapFlow &&
          urlFlow &&
          snapFlow !== urlFlow &&
          hasDeepLinkCourseParams(window.location.search)
        ) {
          clear();
        } else {
          adoptIncomingDdcsResultsUrl();
        }
      }
      return false;
    }

    if (onResults && hasDeepLinkCourseParams(window.location.search)) {
      const snapFlow = snapshotFlowOf();
      const urlFlow =
        inferFlowFromUrl(current) || detectPageFlow();
      if (snapFlow && urlFlow && snapFlow !== urlFlow) {
        clear();
      }
      return false;
    }

    // Peek (do not consume): hide-entry / flow-switch set skip before navigate.
    if (peekSkipRestore()) {
      scheduleConsumeSkipRestore();
      return false;
    }

    if (!onResults) {
      return false;
    }

    // Inactivity confirm is open (or about to open on this same form). Do not
    // inject lastResultsUrl until the user chooses Keep.
    if (shouldHaltUrlRestoreForExpiryPrompt()) {
      return false;
    }

    // Landing-card campus/term hydration or error redirect — do not override
    if (hasIntentionalLandingParams(window.location.search)) {
      return false;
    }

    if (!snapshotMatchesPageFlow()) {
      return false;
    }

    const last = getLastResultsUrl();
    if (!last || !urlHasRestorableCourseState(last)) {
      return false;
    }

    if (normalizeResultsUrl(current) === normalizeResultsUrl(last)) {
      return false;
    }

    try {
      window.history.replaceState({}, '', last);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Seed SelectionManager + React stores from snapshot when the in-memory
   * queue is empty (fresh load). In-page course remove already keeps the
   * live queue — including Suggested/Choice/Bundle — so do not append again.
   * @param {object} selectionManager
   * @returns {boolean}
   */
  function hydrateSelectionManager(selectionManager) {
    if (!selectionManager) {
      return false;
    }
    if (!snapshotMatchesPageFlow()) {
      return false;
    }
    if (expiryPromptPending) {
      return false;
    }
    const existing = Array.isArray(selectionManager.selectedProducts)
      ? selectionManager.selectedProducts
      : [];
    if (existing.length) {
      return false;
    }

    const snap = getSelections();
    if (!snap.length) {
      return false;
    }

    selectionManager.selectedProducts = [];
    const added = appendSnapshotRowsToManager(selectionManager, snap);
    if (added) {
      lastKnownLocalKeys = new Set(
        (selectionManager.selectedProducts || [])
          .map(selectionDedupeKey)
          .filter(Boolean)
      );
    }
    return added;
  }

  /** @param {string} href */
  function isTextbooksEntryHref(href) {
    if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
      return false;
    }
    try {
      const u = new URL(href, window.location.origin);
      if (u.origin !== window.location.origin) return false;
      const path = u.pathname.replace(/\/$/, '') || '/';
      if (path === TEXTBOOKS_LANDING_PATH || path.endsWith(TEXTBOOKS_LANDING_PATH)) {
        return true;
      }
      if (path === RESULTS_PATH || path.endsWith(RESULTS_PATH)) {
        // Bare / campus-term-only results entry — rewrite to last in-progress URL.
        // Opposite-flow `?shopBy=` (hide) must stay blank.
        if (
          hasDeepLinkCourseParams(u.search) ||
          hasIntentionalLandingParams(u.search)
        ) {
          return false;
        }
        const hrefFlow = inferFlowFromUrl(`${u.pathname}${u.search}`);
        const snapFlow = snapshotFlowOf();
        if (hrefFlow && snapFlow && hrefFlow !== snapFlow) {
          return false;
        }
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  function rewriteTextbooksEntryLinks() {
    if (peekSkipRestore()) return;
    if (isHardRefreshDue()) return;
    const last = getLastResultsUrl();
    if (!last || !urlHasRestorableCourseState(last)) return;

    document.querySelectorAll('a[href]').forEach(/** @param {HTMLAnchorElement} a */ a => {
      if (a.hasAttribute('data-cmrp-fresh-start')) return;
      // Intentional flow entry (shop-by / Find Textbooks) must not be rewritten
      // back to a prior in-progress results URL.
      if (a.hasAttribute('data-cmrp-initial-flow')) return;
      const href = a.getAttribute('href');
      if (!isTextbooksEntryHref(href)) return;
      a.setAttribute('data-cmrp-original-href', href || '');
      a.setAttribute('href', last);
      a.setAttribute('data-cmrp-restored-href', 'true');
    });
  }

  function bindTextbooksLinkInterceptor() {
    document.addEventListener(
      'click',
      function (e) {
        if (peekSkipRestore()) return;
        const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
        if (!a || a.hasAttribute('data-cmrp-fresh-start')) return;
        if (a.hasAttribute('data-cmrp-initial-flow')) return;

        const href = a.getAttribute('href');
        if (!isTextbooksEntryHref(href) && !a.hasAttribute('data-cmrp-restored-href')) {
          return;
        }

        if (isHardRefreshDue()) {
          hardRefreshSession();
          const landing =
            a.getAttribute('data-cmrp-original-href') || TEXTBOOKS_LANDING_PATH;
          if (a.getAttribute('href') !== landing) {
            e.preventDefault();
            window.location.assign(landing);
          }
          return;
        }

        // Expiry check before any redirect into stored CMRP state
        const last = getLastResultsUrl();
        if (!last || !urlHasRestorableCourseState(last)) {
          // Undo a prior rewrite so we do not navigate into a cleared session URL
          const original = a.getAttribute('data-cmrp-original-href');
          if (a.hasAttribute('data-cmrp-restored-href') && original) {
            e.preventDefault();
            window.location.assign(original);
          }
          return;
        }

        // Prefer last in-progress results over textbooks landing / bare results
        if (a.getAttribute('href') !== last) {
          e.preventDefault();
          window.location.assign(last);
        }
      },
      true
    );
  }

  function bindSelectionPersistence() {
    document.addEventListener('selection:changed', /** @param {CustomEvent} event */ event => {
      if (applyingRemoteSelections) return;
      const products = event.detail?.selectedProducts;
      if (!Array.isArray(products)) return;
      // Persist regular DDCS cards + Suggested/Choice + Bundle
      saveSelections(products.filter(Boolean));
    });
  }

  /** Other tabs wrote localStorage — align this tab's ATB queue + banner. */
  function bindCrossTabStorageSync() {
    window.addEventListener('storage', /** @param {StorageEvent} event */ event => {
      if (!event.key || event.key !== storageKey()) return;
      scheduleReconcile('storage');
    });
    const channel = ensureSnapshotChannel();
    if (channel) {
      channel.addEventListener('message', () => {
        scheduleReconcile('broadcast');
      });
    }
  }

  /**
   * Confirm ATB queue against localStorage when this CMRP tab is shown again
   * (`storage` is not reliable in every backgrounded browser).
   */
  function bindTabActiveQueueConfirm() {
    const onTabShown = /** @param {string} reason */ reason => {
      if (document.visibilityState && document.visibilityState !== 'visible') {
        return;
      }
      scheduleReconcile(reason);
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      onTabShown('tab-focus');
    });
    window.addEventListener('focus', () => onTabShown('window-focus'));
    window.addEventListener('pageshow', /** @param {PageTransitionEvent} event */ event => {
      if (!event.persisted) return;
      onTabShown('pageshow');
    });
  }

  function bindPageHideSave() {
    window.addEventListener('pagehide', () => {
      // Skip flag means this navigation is hiding a parked snapshot; do not
      // overwrite lastResultsUrl with the outgoing page.
      if (peekSkipRestore()) {
        return;
      }
      const path = window.location.pathname || '';
      if (!path.includes('course-materials-results')) return;
      if (!hasDeepLinkCourseParams(window.location.search)) {
        return;
      }
      saveResultsUrl();
    });
  }

  function init() {
    dropLegacySessionKeys();
    // Expire stale snapshot before any restore / link rewrite (no stamp touch).
    // Hard refresh runs on CMRP visits only, never on in-page interactions,
    // and takes precedence over the inactivity modal.
    // Hard-wipe first so the inactivity modal cannot run on the same visit.
    const hardWiped = applyHardRefreshIfDue();
    if (!hardWiped) {
      clearIfInactive();
    }
    bindSelectionPersistence();
    bindCrossTabStorageSync();
    bindTabActiveQueueConfirm();
    bindPageHideSave();
    bindTextbooksLinkInterceptor();
    // Restore ASAP (head script) so sidebar / URLStateManager see course params
    tryRestoreResultsUrl();
    // Same-flow CMRP visit restarts the inactivity clock unless the expiry
    // modal is waiting (Keep is what resets the stamp).
    if (isCmrpResultsPage() && snapshotMatchesPageFlow() && !expiryPromptPending) {
      touchInteraction();
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', resolveExpiryPromptOnReady);
    } else {
      resolveExpiryPromptOnReady();
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', rewriteTextbooksEntryLinks);
    } else {
      rewriteTextbooksEntryLinks();
    }
  }

  const api = {
    get STORAGE_KEY() {
      return storageKey();
    },
    get SKIP_KEY() {
      return skipKey();
    },
    get ACTIVITY_KEY() {
      return activityKey();
    },
    STORAGE_KEY_BASE,
    SKIP_KEY_BASE,
    ACTIVITY_KEY_BASE,
    get INACTIVITY_MS() {
      return getInactivityMs();
    },
    get HARD_REFRESH_MS() {
      return getHardRefreshMs();
    },
    shopScope,
    scopedKey,
    saveResultsUrl,
    saveSelections,
    getSelections,
    getLastResultsUrl,
    clear,
    clearForFreshStart,
    clearLastResultsUrl,
    removeCourseFromLastResultsUrl,
    prepareFlowSwitch,
    clearIfInactive,
    applyHardRefreshIfDue,
    isHardRefreshDue,
    touchInteraction,
    markSkipRestore,
    consumeSkipRestore,
    tryRestoreResultsUrl,
    isExpiryPromptPending,
    shouldHaltUrlRestoreForExpiryPrompt,
    hydrateSelectionManager,
    rewriteTextbooksEntryLinks,
    hasDeepLinkCourseParams,
    inferFlowFromUrl,
    planFlowEntry,
    detectPageFlow,
    snapshotMatchesPageFlow,
    adoptIfForeignFlow,
    COURSE_PARAM_KEYS,
  };

  window.CmrpSessionSnapshot = api;
  init();
})(window);
